import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AccessTargetNode,
  AccessResourceLoading,
  NativeResources,
  SearchResponseNode,
} from '@ariksa/inventory-core/api';
import { Box, Spinner } from '@chakra-ui/react';
import { uniqBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { CustomTooltip } from 'components/DataDisplay';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { GraphScale } from 'components/Visualization/PixiGraph/common/GraphScale';
import { useGraphDnd } from 'components/Visualization/PixiGraph/hooks/useGraphDnd';
import { useGraphMenuTooltip } from 'components/Visualization/PixiGraph/hooks/useMenuTooltip';
import { useAccessMap } from 'components/Visualization/PixiGraph/maps/AccessMap/hooks/useAccessMap';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import {
  selectActiveCloudAccount,
  selectActiveEnvironment,
} from 'containers/App/selectors';
import { AccessGraphMenu } from 'containers/Visibility/SecurityGraphNext/Components/Access/GraphMenuTooltip';
import { useDownloadMap } from 'containers/Visibility/SecurityGraphNext/hooks/useDownloadMap';
import { useRedirectInfo } from 'containers/Visibility/SecurityGraphNext/hooks/useRedirectInfo';
import { useSearchResult } from 'containers/Visibility/SecurityGraphNext/hooks/useSearchResult';
import { selectSecurityGraphNext } from 'containers/Visibility/SecurityGraphNext/selectors';
import { actions } from 'containers/Visibility/SecurityGraphNext/slice';

interface AccessGraphProps {
  findings?: boolean;
}

export const AccessGraph = (props: AccessGraphProps) => {
  const dispatch = useDispatch();
  const ref = useRef<any>(null);
  const [resourceSearch, setResourceSearch] = useState('');
  const [appliedResourceSearch, setAppliedResourceSearch] = useState('');
  const [selectedResourceTypeNode, setSelectedResourceTypeNode] = useState<
    SearchResponseNode | undefined
  >();
  const [resourceTargets, setResourceTargets] = useState<AccessTargetNode[]>(
    [],
  );
  const selectedResourceType = selectedResourceTypeNode?.native_name ?? '';
  const { getResourceType } = useResourceType();
  const { environmentId } = useSelector(selectActiveEnvironment);
  const { accountId } = useSelector(selectActiveCloudAccount);
  const {
    accessTargets,
    activeSearchQueryTerm,
    currentSearchQuery,
    initialSourceNodeId,
    selectedSourceNodeOption,
    legacyAccessGraph,
  } = useSelector(selectSecurityGraphNext);
  const { searchResult } = useSearchResult();
  const { redirectInfo } = useRedirectInfo();
  const sourceUuid =
    selectedSourceNodeOption?.value?.uuid ?? initialSourceNodeId ?? '';
  const resourceLoading = (searchResult.data as any).access_target_summary
    ?.resource_loading;
  const isOnDemand = resourceLoading === AccessResourceLoading.OnDemand;
  // A resource map draws its own path to the resource. The principal's whole
  // access graph from /map/access would replace it with everything the
  // principal can reach. A stopgap until the server marks such a response
  // complete (plan A18, A19); a map typed in the search bar still loads it.
  const isResourceMap = !!redirectInfo?.identity;
  const usesLegacyResources = !isOnDemand && !isResourceMap;
  const sourceNode = searchResult.data.nodes?.find(
    node => node.uuid === sourceUuid || node.identity === sourceUuid,
  );
  const sourceNativeType =
    selectedSourceNodeOption?.value?.native_name ?? sourceNode?.native_name;
  const sourceAccountId = currentSearchQuery?.account_ids?.[0] ?? accountId;
  const legacyResourceNodes = useMemo(() => {
    if (!usesLegacyResources) return undefined;

    return legacyAccessGraph.data.nodes
      ?.filter(node => node.header === 'Resource')
      .map(node => ({
        ...node,
        id: node.identity,
        is_target: true,
        nodeType: node.native_name,
        ariksaType: getResourceType(node.native_name ?? ''),
      }));
  }, [getResourceType, usesLegacyResources, legacyAccessGraph.data.nodes]);
  const legacyResourcePaths = useMemo(() => {
    if (!usesLegacyResources) return undefined;

    return legacyAccessGraph.data.paths ?? [];
  }, [usesLegacyResources, legacyAccessGraph.data.paths]);
  const resourceNodes = useMemo(() => {
    if (!selectedResourceTypeNode || !isOnDemand) return legacyResourceNodes;

    return resourceTargets.map(target => ({
      ...target,
      id: target.identity,
      header: 'Resource',
      is_target: true,
      nodeType: target.native_name,
      ariksaType: getResourceType(target.native_name),
    }));
  }, [
    getResourceType,
    isOnDemand,
    legacyResourceNodes,
    resourceTargets,
    selectedResourceTypeNode,
  ]);
  const resourcePaths = useMemo(
    () =>
      !isOnDemand && legacyResourcePaths
        ? legacyResourcePaths
        : selectedResourceTypeNode
        ? (resourceNodes ?? []).map(target => ({
            path: [selectedResourceTypeNode.identity, target.identity],
          }))
        : [],
    [isOnDemand, legacyResourcePaths, resourceNodes, selectedResourceTypeNode],
  );
  const resourcePagination = useMemo(() => {
    if (!isOnDemand || !selectedResourceTypeNode) return undefined;
    if (!accessTargets.data.has_more) return null;

    return {
      header: 'Resource',
      visibleCount: resourceTargets.length + 1,
      totalCount: resourceTargets.length + 2,
      cursor: accessTargets.data.next_cursor ?? undefined,
    };
  }, [
    accessTargets.data.has_more,
    accessTargets.data.next_cursor,
    isOnDemand,
    resourceTargets.length,
    selectedResourceTypeNode,
  ]);
  const [searchPosition, setSearchPosition] = useState({
    left: 0,
    top: 0,
    width: 0,
    visible: false,
  });
  const [loadingPosition, setLoadingPosition] = useState({
    left: 0,
    top: 0,
    size: 0,
    visible: false,
  });
  const { app, graph, renderer } = useAccessMap({
    ref,
    findings: props.findings,
    resourceSearch: appliedResourceSearch,
    showResourceSearch: isOnDemand,
    resourceNodes,
    resourcePaths,
    resourcePagination,
    // a resource map keeps its response's own resource count
    resourceHeaderCount: selectedResourceTypeNode
      ? selectedResourceTypeNode.count ?? resourceTargets.length
      : usesLegacyResources
      ? legacyResourceNodes?.length ?? 0
      : undefined,
    loadingNodeId: accessTargets.isLoading
      ? selectedResourceTypeNode?.identity
      : undefined,
  });
  const graphMenu = useGraphMenuTooltip({ app, graph, renderer, ref });
  const { listeners } = useGraphDnd({ ref, app });
  useDownloadMap(app, 'access-map');

  useEffect(() => {
    setSelectedResourceTypeNode(undefined);
    setResourceTargets([]);
    setResourceSearch('');
    setAppliedResourceSearch('');
  }, [activeSearchQueryTerm?.query, activeSearchQueryTerm?.uuid, sourceUuid]);

  useEffect(() => {
    if (
      !usesLegacyResources ||
      !sourceUuid ||
      !sourceNativeType ||
      !sourceAccountId
    ) {
      return;
    }

    dispatch(
      actions.getLegacyAccessGraph({
        q: {
          uuid: sourceUuid,
          accountId: sourceAccountId,
          nativeResourceType: sourceNativeType as NativeResources,
        },
      }),
    );
  }, [
    dispatch,
    usesLegacyResources,
    sourceAccountId,
    sourceNativeType,
    sourceUuid,
  ]);

  const handleResourceTypeClick = useCallback(
    (
      resourceTypeNode: SearchResponseNode,
      searchText?: string,
      cursor?: string | null,
    ) => {
      const resourceType = resourceTypeNode.native_name;
      if (!resourceType || !sourceUuid) return;

      const isResourceTypeNodeClick =
        searchText === undefined && cursor === undefined;
      if (
        isResourceTypeNodeClick &&
        selectedResourceTypeNode?.identity === resourceTypeNode.identity
      ) {
        setSelectedResourceTypeNode(undefined);
        setResourceTargets([]);
        setResourceSearch('');
        setAppliedResourceSearch('');
        return;
      }

      setSelectedResourceTypeNode(resourceTypeNode);
      if (!cursor) {
        setResourceTargets([]);
        setAppliedResourceSearch(searchText?.trim() ?? '');
      }
      if (searchText === undefined) setResourceSearch('');
      if (!isOnDemand) return;
      dispatch(
        actions.getAccessTargets({
          q: {
            accessTargetQuery: {
              query: activeSearchQueryTerm?.query ?? currentSearchQuery?.query,
              uuid: activeSearchQueryTerm?.uuid ?? currentSearchQuery?.uuid,
              wrapper: currentSearchQuery?.wrapper,
              source_uuid: sourceUuid,
              account_id: currentSearchQuery?.account_ids || undefined,
              environment_id:
                currentSearchQuery?.environment_id ?? environmentId,
              resource_type: resourceType,
              search_text: searchText?.trim() || undefined,
              page_size: 25,
              cursor,
            },
          },
          onSuccess: response => {
            const targets = response?.targets ?? [];
            setResourceTargets(currentTargets =>
              cursor
                ? uniqBy([...currentTargets, ...targets], 'identity')
                : targets,
            );
          },
        }),
      );
    },
    [
      activeSearchQueryTerm?.query,
      activeSearchQueryTerm?.uuid,
      currentSearchQuery?.account_ids,
      currentSearchQuery?.environment_id,
      currentSearchQuery?.query,
      currentSearchQuery?.uuid,
      currentSearchQuery?.wrapper,
      dispatch,
      environmentId,
      isOnDemand,
      selectedResourceTypeNode?.identity,
      sourceUuid,
    ],
  );

  const handleResourceSearchChange = useCallback((searchText: string) => {
    setResourceSearch(searchText);
  }, []);

  const handleResourcePaginationClick = useCallback(
    (cursor?: string | null) => {
      if (!selectedResourceTypeNode || !cursor) return;
      handleResourceTypeClick(
        selectedResourceTypeNode,
        appliedResourceSearch,
        cursor,
      );
    },
    [appliedResourceSearch, handleResourceTypeClick, selectedResourceTypeNode],
  );

  useEffect(() => {
    const updateSearchPosition = () => {
      const header = graph.headers.find(item => item.headerName === 'Resource');
      const scale = app.stage.scale.x;
      if (header) {
        const nextPosition = {
          left: app.stage.x + header.x * scale,
          top: app.stage.y + (header.y + header.h) * scale + 8,
          width: header.w * scale,
          visible: true,
        };

        setSearchPosition(current =>
          current.left === nextPosition.left &&
          current.top === nextPosition.top &&
          current.width === nextPosition.width &&
          current.visible === nextPosition.visible
            ? current
            : nextPosition,
        );
      }

      const loadingNode = graph.nodes.find(
        item => item.id === selectedResourceTypeNode?.identity,
      );
      const nextLoadingPosition =
        accessTargets.isLoading && loadingNode
          ? {
              left: app.stage.x + (loadingNode.x + 24) * scale,
              top: app.stage.y + (loadingNode.y + loadingNode.h / 2) * scale,
              size: Math.max(12, 18 * scale),
              visible: true,
            }
          : { left: 0, top: 0, size: 0, visible: false };

      setLoadingPosition(current =>
        current.left === nextLoadingPosition.left &&
        current.top === nextLoadingPosition.top &&
        current.size === nextLoadingPosition.size &&
        current.visible === nextLoadingPosition.visible
          ? current
          : nextLoadingPosition,
      );
    };

    app.ticker.add(updateSearchPosition);
    return () => {
      app.ticker.remove(updateSearchPosition);
    };
  }, [
    accessTargets.isLoading,
    app,
    graph,
    isOnDemand,
    selectedResourceTypeNode?.identity,
  ]);

  return (
    <Box
      h={'full'}
      w={'full'}
      pos={'absolute'}
      onContextMenu={e => e.preventDefault()}
    >
      <Box
        className="graph-canvas-v1"
        ref={ref}
        {...listeners}
        {...graphMenu.listeners}
      ></Box>
      {isOnDemand && searchPosition.visible && (
        <Box
          position="absolute"
          left={`${searchPosition.left}px`}
          top={`${searchPosition.top}px`}
          w={`${searchPosition.width}px`}
          minW="150px"
          zIndex={2}
          onPointerDown={event => event.stopPropagation()}
        >
          <CustomTooltip
            label={
              !selectedResourceType
                ? 'Click on resource type to enable search'
                : undefined
            }
            styles={{
              text: { w: 'full' },
              tooltip: { w: 32, textAlign: 'center' },
            }}
          >
            <SearchBox
              value={resourceSearch}
              onChange={handleResourceSearchChange}
              onSearch={searchText =>
                selectedResourceTypeNode &&
                handleResourceTypeClick(selectedResourceTypeNode, searchText)
              }
              placeholder="Type & enter to search..."
              showShortcut={false}
              bg="white"
              isDisabled={!selectedResourceType}
              isLoading={accessTargets.isLoading}
            />
          </CustomTooltip>
        </Box>
      )}
      {loadingPosition.visible && (
        <Spinner
          position="absolute"
          left={`${loadingPosition.left - 10}px`}
          top={`${loadingPosition.top - 10}px`}
          boxSize={`${loadingPosition.size}px`}
          transform="translate(-50%, -50%)"
          color="primary"
          thickness="2px"
          pointerEvents="none"
          zIndex={3}
        />
      )}
      <AccessGraphMenu
        app={app}
        graph={graph}
        renderer={renderer}
        onResourceTypeClick={handleResourceTypeClick}
        onResourcePaginationClick={handleResourcePaginationClick}
      />
      <GraphScale app={app} graph={graph} />
    </Box>
  );
};
